import { useSetStateAction, useValueEqualMemo } from "client/Hooks";
import { generateTrackingId, isNotNil } from "client/Misc/utils";
import {
	Command,
	EditorState,
	Plugin
} from "prosemirror-state";
import {
	Dispatch,
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	SetStateAction,
	useCallback,
	useContext,
	useImperativeHandle,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { ControlAttachmentOptions } from "../ControlAttachment";
import { ProsemirrorView, ProsemirrorViewHandle } from "../ProsemirrorView/ProsemirrorView";
import { insertSuggestion } from "./commands/insertSuggestion";
import { insertText } from "./commands/insertText";
import { mentionInputSchema } from "./schema";
import { MentionInputSuggestionList } from "./SuggestionList";
import {
	isProseMirror,
	MentionInputState,
	MentionInputStateKind,
	ProseMirrorMentionInputState
} from "./types";
import styles from "./MentionInput.module.less";
import { baseKeymap } from "prosemirror-commands";
import { buildKeymap } from "./keymap";
import { keymap } from "prosemirror-keymap";
import { history } from "prosemirror-history";
import { never } from "client_legacy/ndaw/errors";
import { isEmptyContent } from "./utils";
import { TextWithMentions } from "./converters";
import classNames from "classnames";
import {
	MentionInputSuggestion,
	MentionInputSuggestionBoxHandle,
	MentionInputSuggestionBoxProps,
	useMentions
} from "./useMentions";
import { UniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";
import { linkifyPlugin } from "./plugins/linkifyPlugin";

export type {
	MentionInputSuggestion,
	MentionInputSuggestionBoxHandle,
	MentionInputSuggestionBoxProps
};

const {
	"Enter": _,
	...baseKeymapWithoutEnterBinding
} = baseKeymap;

export type MentionInputConfiguration = {
	shouldHighlightUrls: boolean;
};

export function createInitialInputValue(
	configuration: MentionInputConfiguration
): ProseMirrorMentionInputState {
	const {
		shouldHighlightUrls
	} = configuration;

	const plugins: Plugin[] = [
		keymap(buildKeymap()),
		keymap(baseKeymapWithoutEnterBinding),
		history()
	];

	if (shouldHighlightUrls) {
		plugins.push(linkifyPlugin());
	}

	return {
		kind: MentionInputStateKind.ProseMirror,
		editorState: EditorState.create({
			schema: mentionInputSchema,
			plugins
		})
	};
}

export const initialInputValue: MentionInputState = createInitialInputValue({
	shouldHighlightUrls: true
});

export type MentionInputProps = {
	value: MentionInputState | undefined;
	onChange(newState: MentionInputState): void;
	mentionTypes: string | readonly string[];
	renderSuggestionBox(
		props: MentionInputSuggestionBoxProps,
		ref: Ref<MentionInputSuggestionBoxHandle>
	): ReactElement;
	trackingId: string;
	onBlur?(value: MentionInputState): void;
	onFocus?(value: MentionInputState): void;
	onInsertSuggestion?(
		suggestion: MentionInputSuggestion,
		mentionType: string
	): void;
	placeholder?: string;
	isInvalid?: boolean;
	isDisabled?: boolean;
	isBorderless?: boolean;
	hasNoPadding?: boolean;
	hasNoBackgroundColor?: boolean;
	maxHeight?: number;
	autoFocus?: "focus";
	attachedOn?: ControlAttachmentOptions;
} & ({
	height?: "fill" | "resizable" | number;
	initialHeight?: undefined;
} | {
	height: "auto";
	initialHeight?: "default" | "large";
});

export type MentionInputHandle = {
	focus: () => void;
	insert: (text: string) => void;
};

const MentionInputInternal = forwardRef(
	(props: MentionInputProps, ref: Ref<MentionInputHandle>): ReactElement => {
		const {
			value = initialInputValue,
			onChange,
			renderSuggestionBox,
			trackingId,
			attachedOn,
			hasNoBackgroundColor,
			hasNoPadding,
			height,
			maxHeight,
			autoFocus,
			isBorderless,
			isDisabled,
			isInvalid,
			onBlur,
			onFocus,
			onInsertSuggestion,
			placeholder,
			initialHeight = "default"
		} = props;

		const mentionTypes = useValueEqualMemo(
			(): readonly string[] => {
				return typeof props.mentionTypes === "string"
					? [props.mentionTypes]
					: props.mentionTypes;
			},
			[props.mentionTypes]
		);

		if (!isProseMirror(value)) {
			throw new Error("This component only works with prosemirror state");
		}

		const viewRef: RefObject<ProsemirrorViewHandle | null> = useRef<ProsemirrorViewHandle>(null);
		const { id } = useContext(UniqueInputIdContext);

		const {
			suggestionBox,
			keyDownHandler,
			textInputHandler
		} = useMentions({
			editorState: value.editorState,
			insertSuggestion,
			mentionTypes,
			renderSuggestionBox,
			viewRef,
			onInsertSuggestion,
			trackingId: generateTrackingId(trackingId, "__suggestion-list")
		});

		const stateChangeHandler = useCallback(
			(prosemirrorState: EditorState): void => {
				onChange({
					kind: MentionInputStateKind.ProseMirror,
					editorState: prosemirrorState
				});
			},
			[onChange]
		);

		const isEditable = useCallback(
			(): boolean => {
				return !isDisabled;
			},
			[isDisabled]
		);

		const blurHandler = useCallback(
			(): void => {
				onBlur?.(value);
			},
			[onBlur, value]
		);

		const focusHandler = useCallback(
			(): void => {
				onFocus?.(value);
			},
			[onFocus, value]
		);

		const setEditorState: Dispatch<SetStateAction<EditorState>> = useSetStateAction(
			value.editorState,
			(newEditorState: EditorState): void => {
				onChange({
					kind: MentionInputStateKind.ProseMirror,
					editorState: newEditorState
				});
			}
		);

		const insertHandler = useCallback(
			(text: string): void => {
				const command: Command = insertText(text);

				setEditorState(
					(currentEditorState: EditorState): EditorState => {
						let updatedEditorState: EditorState = currentEditorState;

						command(
							currentEditorState,
							(tr): void => {
								updatedEditorState = updatedEditorState.apply(tr);
							}
						);

						return updatedEditorState;
					}
				);

				viewRef.current
					?.focus();
			},
			[setEditorState]
		);

		useImperativeHandle(
			ref,
			(): MentionInputHandle => ({
				focus: (): void => {
					viewRef.current
						?.focus();
				},
				insert: insertHandler,
			}),
			[insertHandler]
		);

		useLayoutEffect(
			(): void => {
				const { current: handle } = viewRef;
				if (handle && isNotNil(autoFocus)) {
					switch (autoFocus) {
						case "focus":
							handle.focus();
							break;
						default:
							never(autoFocus);
					}
				}
			},
			[autoFocus]
		);

		const shouldRenderPlaceholder = useMemo(
			(): boolean => {
				return isEmptyContent(value.editorState);
			},
			[value.editorState]
		);

		const isSuggestionBoxOpen: boolean = isNotNil(suggestionBox);

		return (
			<>
				<ProsemirrorView
					ref={viewRef}
					trackingId={trackingId}
					attachedOn={attachedOn}
					hasNoBackgroundColor={hasNoBackgroundColor}
					hasNoPadding={hasNoPadding}
					height={height}
					maxHeight={maxHeight}
					isBorderless={isBorderless}
					isDisabled={isDisabled}
					isInvalid={isInvalid}
					placeholder={placeholder}
					onBlur={blurHandler}
					onFocus={focusHandler}
					additionalClassName={
						classNames(
							styles.root,
							{
								[styles.root_initialSizeLarge]: initialHeight === "large",
								[styles.root_active]: isSuggestionBoxOpen,
								[styles.root_isBorderless]: isBorderless
							}
						)
					}
					editorState={value.editorState}
					onChange={stateChangeHandler}
					handleTextInput={textInputHandler}
					handleKeyDown={keyDownHandler}
					editable={isEditable}
					shouldRenderPlaceholder={shouldRenderPlaceholder}
					id={id}
				/>
				{suggestionBox}
			</>
		);
	}
);
MentionInputInternal.displayName = "MentionInput";

type MentionInputWithSubComponents = typeof MentionInputInternal & {
	SuggestionList: typeof MentionInputSuggestionList;
};

export const MentionInput: MentionInputWithSubComponents = MentionInputInternal as unknown as MentionInputWithSubComponents;
MentionInput.SuggestionList = MentionInputSuggestionList;

export type MentionInputIntermediateStateHandlers = {
	intermediateState: MentionInputState;
	setIntermediateState: Dispatch<SetStateAction<MentionInputState>>;
	getModelThatRepresentsTheState(): TextWithMentions;
	setModelThatRepresentsTheState(model: TextWithMentions): void;
};

export function useMentionInputIntermediateState(
	initialState: MentionInputState = initialInputValue
): MentionInputIntermediateStateHandlers {
	const [intermediateState, setIntermediateState] = useState<MentionInputState>(initialState);
	const modelThatRepresentsTheStateRef: RefObject<TextWithMentions> = useRef<TextWithMentions>({
		text: "",
		mentions: []
	});

	const result = useMemo(
		(): MentionInputIntermediateStateHandlers => {
			return {
				intermediateState,
				setIntermediateState,
				getModelThatRepresentsTheState(): TextWithMentions {
					return modelThatRepresentsTheStateRef.current;
				},
				setModelThatRepresentsTheState(model: TextWithMentions): void {
					modelThatRepresentsTheStateRef.current = model;
				}
			};
		},
		[intermediateState]
	);

	return result;
}